/**
 * Typed wrapper over the official Telegram Mini Apps script (telegram-web-app.js, PLAN.md D22).
 * Screens never touch `window.Telegram` directly; they use these helpers and the hooks in /hooks.
 */

type HapticImpact = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';
type HapticNotification = 'error' | 'success' | 'warning';

export interface BottomButton {
  setParams(params: {
    text?: string;
    color?: string;
    text_color?: string;
    is_active?: boolean;
    is_visible?: boolean;
    has_shine_effect?: boolean;
    /** SecondaryButton only (Bot API 7.10): where it sits relative to the MainButton. */
    position?: 'left' | 'right' | 'top' | 'bottom';
  }): BottomButton;
  onClick(cb: () => void): BottomButton;
  offClick(cb: () => void): BottomButton;
  show(): BottomButton;
  hide(): BottomButton;
  showProgress(leaveActive?: boolean): BottomButton;
  hideProgress(): BottomButton;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { start_param?: string; user?: { id: number; first_name: string } };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  ready(): void;
  expand(): void;
  close(): void;
  isVersionAtLeast(version: string): boolean;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  setBottomBarColor?(color: string): void;
  disableVerticalSwipes?(): void;
  MainButton: BottomButton;
  /** Bot API 7.10+. */
  SecondaryButton?: BottomButton;
  BackButton: {
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
  HapticFeedback: {
    impactOccurred(style: HapticImpact): void;
    notificationOccurred(type: HapticNotification): void;
    selectionChanged(): void;
  };
  showConfirm(message: string, cb: (ok: boolean) => void): void;
  /** Bot API 6.9+. */
  requestContact?(cb?: (shared: boolean) => void): void;
  openTelegramLink(url: string): void;
  openLink(url: string): void;
  downloadFile?(params: { url: string; file_name: string }, cb?: (accepted: boolean) => void): void;
  onEvent(event: string, cb: () => void): void;
  offEvent(event: string, cb: () => void): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function webApp(): TelegramWebApp | undefined {
  return typeof window === 'undefined' ? undefined : window.Telegram?.WebApp;
}

/** Signed launch data, empty outside Telegram. Sent to POST /auth/telegram. */
export function rawInitData(): string {
  return webApp()?.initData ?? '';
}

/** True inside a real Telegram client, where native buttons are rendered by Telegram. */
export function hasNativeControls(): boolean {
  const app = webApp();
  return Boolean(app && app.platform !== 'unknown' && app.isVersionAtLeast('6.1'));
}

export function startParam(): string | undefined {
  return webApp()?.initDataUnsafe.start_param;
}

/** Called once at boot: tells Telegram the app is ready and matches its chrome to the page. */
export function initTelegram(): void {
  const app = webApp();
  if (!app) return;
  app.ready();
  app.expand();
  if (app.isVersionAtLeast('7.7')) app.disableVerticalSwipes?.();
  if (app.isVersionAtLeast('6.1')) {
    app.setHeaderColor('secondary_bg_color');
    app.setBackgroundColor('secondary_bg_color');
  }
  if (app.isVersionAtLeast('7.10')) app.setBottomBarColor?.('secondary_bg_color');
}

export const haptic = {
  light: () => webApp()?.HapticFeedback.impactOccurred('light'),
  selection: () => webApp()?.HapticFeedback.selectionChanged(),
  success: () => webApp()?.HapticFeedback.notificationOccurred('success'),
  error: () => webApp()?.HapticFeedback.notificationOccurred('error'),
};

/** Native confirm dialog in Telegram, window.confirm elsewhere. */
export function confirmDialog(message: string): Promise<boolean> {
  const app = webApp();
  if (app && hasNativeControls()) {
    return new Promise((resolve) => app.showConfirm(message, resolve));
  }
  return Promise.resolve(window.confirm(message));
}

/**
 * Telegram's own "share your phone number" dialog (Bot API 6.9). The number reaches the bot as the
 * seller's contact, which the bot stores (spec 7.3.3). Resolves true when the seller shared it.
 */
export function requestContact(): Promise<boolean> {
  const app = webApp();
  if (!app?.requestContact || !hasNativeControls() || !app.isVersionAtLeast('6.9')) {
    return Promise.resolve(false);
  }
  return new Promise((resolve) => {
    try {
      app.requestContact?.((shared) => resolve(shared));
    } catch {
      resolve(false);
    }
  });
}

/** Opens a t.me link inside Telegram (keeps the Mini App open), a new tab elsewhere. */
export function openTelegramLink(url: string): void {
  const app = webApp();
  if (app && hasNativeControls()) app.openTelegramLink(url);
  else window.open(url, '_blank', 'noopener');
}

/** Opens an external link in the in-app browser inside Telegram, a new tab elsewhere. */
export function openLink(url: string): void {
  const app = webApp();
  if (app && hasNativeControls()) {
    if (url.startsWith('https://t.me/')) app.openTelegramLink(url);
    else app.openLink(url);
  } else window.open(url, '_blank', 'noopener');
}

/** Telegram on a phone or tablet; desktop and web clients report other platforms. */
function isMobileTelegram(): boolean {
  const platform = webApp()?.platform;
  return platform === 'ios' || platform === 'android' || platform === 'android_x';
}

/**
 * Saves a file. Phones get Telegram's native download dialog (Bot API 8.0). Desktop Telegram does
 * not save files that way (the macOS app ignores the request), so desktops, web clients and a
 * plain browser open `attachmentUrl` instead, which answers with the file as an attachment (D44).
 */
export function downloadFile(url: string, fileName: string, attachmentUrl: string): void {
  const app = webApp();
  if (app?.downloadFile && isMobileTelegram() && app.isVersionAtLeast('8.0')) {
    try {
      app.downloadFile({ url, file_name: fileName });
      return;
    } catch {
      // A dialog Telegram never answered blocks the next request; the browser still works.
    }
  }
  openLink(attachmentUrl);
}

/** Copies text to the clipboard; falls back to a hidden textarea for older webviews. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    area.remove();
    return ok;
  }
}
